import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  'experience-candidate-button experience-candidate-motion experience-candidate-focus inline-flex min-w-0 items-center justify-center gap-2 rounded-lg text-center font-semibold leading-tight whitespace-normal break-words [overflow-wrap:anywhere] transition-interactive duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'experience-candidate-button-primary border border-statura-green-accessible bg-statura-green-accessible text-white shadow-sm hover:bg-statura-green-accessible-hover focus-visible:ring-statura-green-accessible',
        secondary:
          'experience-candidate-button-secondary border border-slate-300 bg-white text-statura-ink-dark shadow-sm hover:border-slate-400 hover:bg-slate-50 focus-visible:ring-statura-ink-dark',
        ghost:
          'experience-candidate-button-ghost bg-transparent text-statura-ink-dark font-medium hover:bg-slate-100 focus-visible:ring-statura-ink-dark',
        danger:
          'experience-candidate-button-danger border border-red-600 bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-600',
        link: 'experience-candidate-button-link text-statura-green-accessible underline-offset-4 hover:underline hover:text-statura-green-accessible-hover p-0',
      },
      size: {
        default: 'min-h-10 px-4 py-2.5 text-sm',
        sm: 'min-h-8 px-3 py-1.5 text-xs',
        lg: 'min-h-11 px-5 py-3 text-sm',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, children, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        data-experience-button-variant={variant ?? 'primary'}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="experience-candidate-motion h-4 w-4 animate-spin" />
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
